import type { inferRouterOutputs } from "@trpc/server";
import { defineMessages, type IntlShape } from "react-intl";
import { validateBrandStrategyInput } from "@runcheetah/shared";

import type { AppRouter } from "@runcheetah/api/router";

type StrategyOutput = inferRouterOutputs<AppRouter>["strategy"]["getMine"];

export type BrandStrategyView = NonNullable<StrategyOutput>;

export type StrategyEditorState = {
  postsPerDay: number;
  contentPillars: BrandStrategyView["contentPillars"];
  campaignMix: BrandStrategyView["campaignMix"];
};

const strategyStatusMessages = defineMessages({
  needsAttention: {
    id: "strategy.status.needsAttention",
    defaultMessage: "Needs attention",
  },
  refreshingExplanation: {
    id: "strategy.status.refreshingExplanation",
    defaultMessage: "Refreshing explanation",
  },
  ready: {
    id: "strategy.status.ready",
    defaultMessage: "Ready",
  },
});

export const toStrategyEditorState = (strategy: BrandStrategyView): StrategyEditorState => ({
  postsPerDay: strategy.postsPerDay,
  contentPillars: strategy.contentPillars.toSorted((a, b) => b.percentage - a.percentage),
  campaignMix: strategy.campaignMix.toSorted((a, b) => b.percentage - a.percentage),
});

export const sumPercentages = (items: Array<{ percentage: number }>) =>
  items.reduce((sum, item) => sum + item.percentage, 0);

export const getStrategyStatusLabel = (strategy: BrandStrategyView, intl: IntlShape) => {
  if (!strategy.isValid) {
    return intl.formatMessage(strategyStatusMessages.needsAttention);
  }

  if (strategy.reasoningState === "processing") {
    return intl.formatMessage(strategyStatusMessages.refreshingExplanation);
  }

  return intl.formatMessage(strategyStatusMessages.ready);
};

export const hasReasoningWarning = (strategy: BrandStrategyView) =>
  strategy.reasoningState === "stale" || strategy.reasoningState === "failed";

export const validateStrategyEditorState = (state: StrategyEditorState) =>
  validateBrandStrategyInput({
    postsPerDay: state.postsPerDay,
    contentPillars: state.contentPillars,
    campaignMix: state.campaignMix,
  });
